import type { CatalogProductMediaProjection } from "../gateway/catalog-price-client";
import type { CatalogMediaDelivery } from "../gateway/catalog-media-client";
import { posRuntimeDb, type CatalogMediaRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export function catalogMediaScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:catalog-media`;
}

export function catalogMediaReferenceKey(mediaAssetId: string, contentRevision: string) {
  return `${mediaAssetId}:${contentRevision.toLowerCase()}`;
}

export function catalogMediaRecordKey(
  identity: TrustedTerminalIdentity,
  mediaAssetId: string,
  contentRevision: string,
) {
  return `catalog-media:${catalogMediaScope(identity)}:asset:${catalogMediaReferenceKey(mediaAssetId, contentRevision)}`;
}

function bytesToHex(bytes: ArrayBuffer) {
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(blob: Blob) {
  return bytesToHex(await crypto.subtle.digest("SHA-256", await blob.arrayBuffer()));
}

export async function readCatalogMediaForScope(identity: TrustedTerminalIdentity) {
  return posRuntimeDb.catalogMedia
    .where("scopeKey")
    .equals(catalogMediaScope(identity))
    .toArray();
}

export async function readCatalogMediaReference(
  identity: TrustedTerminalIdentity,
  media: Pick<CatalogProductMediaProjection, "media_asset_id" | "content_revision">,
) {
  return posRuntimeDb.catalogMedia.get(
    catalogMediaRecordKey(identity, media.media_asset_id, media.content_revision),
  );
}

export async function storeVerifiedCatalogMedia(input: {
  identity: TrustedTerminalIdentity;
  expected: CatalogProductMediaProjection;
  delivery: CatalogMediaDelivery;
  blob: Blob;
}) {
  const { identity, expected, delivery, blob } = input;
  const expectedRevision = expected.content_revision.toLowerCase();
  const deliveredRevision = delivery.content_revision.toLowerCase();

  if (
    delivery.media_asset_id !== expected.media_asset_id ||
    deliveredRevision !== expectedRevision
  ) {
    throw new Error("CATALOG_MEDIA_REFERENCE_MISMATCH");
  }

  if (expected.byte_size > 0 && blob.size !== expected.byte_size) {
    throw new Error("CATALOG_MEDIA_BYTE_SIZE_MISMATCH");
  }
  if (delivery.byte_size && blob.size !== delivery.byte_size) {
    throw new Error("CATALOG_MEDIA_DELIVERY_BYTE_SIZE_MISMATCH");
  }

  const actualRevision = await sha256Hex(blob);
  if (actualRevision !== expectedRevision) {
    throw new Error("CATALOG_MEDIA_CONTENT_REVISION_MISMATCH");
  }

  const scopeKey = catalogMediaScope(identity);
  const record: CatalogMediaRecord = {
    key: catalogMediaRecordKey(identity, expected.media_asset_id, expectedRevision),
    scopeKey,
    mediaAssetId: expected.media_asset_id,
    contentRevision: expectedRevision,
    mimeType: delivery.mime_type || expected.mime_type || blob.type || "application/octet-stream",
    byteSize: blob.size,
    blob,
    updatedAt: new Date().toISOString(),
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.catalogMedia, async () => {
    // Content revision is immutable. When the same asset is republished with a new
    // revision, remove the stale revision for this exact terminal scope only.
    const prior = await posRuntimeDb.catalogMedia
      .where("[scopeKey+mediaAssetId]")
      .equals([scopeKey, expected.media_asset_id])
      .toArray();
    const staleKeys = prior
      .filter((row) => row.contentRevision !== expectedRevision)
      .map((row) => row.key);
    if (staleKeys.length) await posRuntimeDb.catalogMedia.bulkDelete(staleKeys);
    await posRuntimeDb.catalogMedia.put(record);
  });

  return record;
}

export async function pruneCatalogMediaScope(
  identity: TrustedTerminalIdentity,
  required: Array<Pick<CatalogProductMediaProjection, "media_asset_id" | "content_revision">>,
) {
  const scopeKey = catalogMediaScope(identity);
  const retained = new Set(
    required.map((media) => catalogMediaReferenceKey(media.media_asset_id, media.content_revision)),
  );
  const current = await posRuntimeDb.catalogMedia.where("scopeKey").equals(scopeKey).toArray();
  const staleKeys = current
    .filter((row) => !retained.has(catalogMediaReferenceKey(row.mediaAssetId, row.contentRevision)))
    .map((row) => row.key);
  if (staleKeys.length) await posRuntimeDb.catalogMedia.bulkDelete(staleKeys);
}

export async function purgeCatalogMediaOutsideScope(identity: TrustedTerminalIdentity) {
  const currentScope = catalogMediaScope(identity);
  const staleKeys = await posRuntimeDb.catalogMedia
    .filter((row) => row.scopeKey !== currentScope)
    .primaryKeys();
  if (staleKeys.length) await posRuntimeDb.catalogMedia.bulkDelete(staleKeys);
}
